import React, { useState, useEffect, useRef } from 'react'

import { BonusModal, BonusModalHeader, BonusModalBody, BonusModalFooter } from '@components'
import { buildGradientStyle } from '@utils/buildGradientStyle'
import { buildGradientBorderStyle } from '@utils/buildGradientBorderStyle'

import {
    useConfigurationStore,
    useBetSliderStore,
    usePlayerInfoStore,
    useBuyBonusStore,
    useDoubleChanceStore,
} from '@stores'

import buyButton from '@assets/buy-bonus/button-buy-enabled.svg?inline'
import toggleCircle from '@assets/buy-bonus/toggle-circle.svg?inline'
import {
    BuyBonusConfirmButtonClickedMessage,
    BuyBonusDecreaseButtonClickedMessage,
    BuyBonusFeature,
    BuyBonusGuaranteeButtonClickedMessage,
    BuyBonusIncreaseButtonClickedMessage,
    BuyBonusMessages,
    BuyBonusRejectedMessage,
    DoubleChanceButtonClickedMessage,
    EngineBetSliderSetScrollingIndexMessage,
    GameUIMessageScope,
    GameUIMessageType,
    ViewportType,
} from 'game-ui-types'
import { ArrowButtonState, ArrowButtonType } from '@constants/buyBonus.ts'
import { buildAtlasImageStyles } from '@utils/buildAtlasImageStyles'
import { useFormatMoney } from '@hooks'
import FeatureOption from './FeatureOption'
import { BuyBonusOptionButtonClickedMessage } from 'game-ui-types'
import { gameAnalytics } from '@utils/gameAnalytics'
import { getBetValue } from '@utils/betOption'
import { useTranslation } from '@amusnet/translations/react'
import { cn } from '@amusnet/react-ui'
import { FeatureGroup } from '@constants/buyBonus'

interface BuyBonusModalProps {
    isOpen: boolean
    onClose: () => void
}

const BuyBonusModal: React.FC<BuyBonusModalProps> = ({ isOpen, onClose }) => {
    const { t } = useTranslation()
    const formatMoney = useFormatMoney()
    const balance = usePlayerInfoStore((state) => state.balance)
    const subtractedBalance = usePlayerInfoStore((state) => state.subtractedBalance)

    const betOptions = useConfigurationStore((state) => state.betOptions)
    const activeIndex = useBetSliderStore((state) => state.activeIndex)
    const updateScrollToIndex = useBetSliderStore((state) => state.updateScrollToIndex)
    const currency = useConfigurationStore((state) => state.currency)
    const viewport = useConfigurationStore((state) => state.viewport)
    const buyBonusConfig = useConfigurationStore((state) => state.buyBonusConfig)
    const shouldBalanceSubtractOnly = useConfigurationStore((state) => state.shouldBalanceSubtractOnly)
    const setDoubleChanceMultiplier = useConfigurationStore((state) => state.setDoubleChanceMultiplier)

    const findElementByImageName = useBuyBonusStore((state) => state.findElementByImageName)
    const buyBonusStoreConfig = useBuyBonusStore((state) => state.buyBonusConfig)
    const { maxBet, layout, features: rawFeatures } = buyBonusStoreConfig
    const setSelectedFeature = useBuyBonusStore((state) => state.setSelectedFeature)
    const setMultiChoiceToggle = useBuyBonusStore((state) => state.setMultiChoiceToggle)
    const setIsDoubleChanceActivated = useDoubleChanceStore((state) => state.setIsDoubleChanceActivated)
    const updateCurrentFeatureMultiplier = useBuyBonusStore((state) => state.updateCurrentFeatureMultiplier)
    const hasGuaranteedWin = useBuyBonusStore((state) => state.hasGuaranteedWin)
    const selectedFeatureIndex = useBuyBonusStore((state) => state.selectedFeatureIndex)
    const isGuaranteedWinEnabled = useBuyBonusStore((state) => state.isGuaranteedWinEnabled)
    const setIsGuaranteedWinEnabled = useBuyBonusStore((state) => state.setIsGuaranteedWinEnabled)
    const isForceGuaranteedWinEnabled = useBuyBonusStore((state) => state.isForceGuaranteedWinEnabled)
    const setIsForceGuaranteedWinEnabled = useBuyBonusStore((state) => state.setIsForceGuaranteedWinEnabled)
    const setSelectedFeatureIndex = useBuyBonusStore((state) => state.setSelectedFeatureIndex)
    const cachedImages = useBuyBonusStore((state) => state.cachedImages)

    const [activeBetIndex, setActiveBetIndex] = useState(activeIndex)
    const [rightArrowState, setRightArrowState] = useState(ArrowButtonState.ENABLED)
    const [leftArrowState, setLeftArrowState] = useState(ArrowButtonState.ENABLED)
    const [isRightArrowDisabled, setIsRightArrowDisabled] = useState(false)
    const [isLeftArrowDisabled, setIsLeftArrowDisabled] = useState(false)
    const [confirmButtonDisabled, setConfirmButtonDisabled] = useState(false)

    const activeBet = betOptions[activeBetIndex]

    const scrollRef = React.useRef<HTMLDivElement | null>(null)

    const portraitViewport = viewport === ViewportType.MOBILE_PORTRAIT
    const isDownRef = useRef(false)
    const isDraggingRef = useRef(false)
    const startXRef = useRef(0)
    const startScrollLeftRef = useRef(0)
    const pointerIdRef = useRef<number | null>(null)
    const cancelNextClickRef = useRef(false)
    const isDesktop = viewport === ViewportType.DESKTOP

    const DRAG_THRESHOLD_X = 6
    const LANDSCAPE_SCALE = 0.72
    const scrollThreshold = { portrait: 3, landscape: 6 }

    const groupToIconLabelMap: Record<number, string> = {
        [FeatureGroup.BuyBonus]: 'buy-bonus-image-card-0',
        [FeatureGroup.ChancePlus]: 'chance-image-card-0',
    }

    const features = rawFeatures.map((feature) => ({
        featureGroup: FeatureGroup.BuyBonus,
        ...feature,
    }))
    const hasChancePlus = features.filter((feature) => feature.featureGroup === FeatureGroup.ChancePlus).length > 0
    const isMultiChoice =
        features.filter((feature) => feature.featureGroup === FeatureGroup.BuyBonus).length > 1 || hasChancePlus

    // `selectedFeatureIndex` is persisted in the store and can outlive a shrinking
    // feature list (e.g. switching from a multi-choice to a single-choice config),
    // leaving it out of range. Clamp it so reads never hit `undefined`.
    const safeSelectedFeatureIndex = selectedFeatureIndex < features.length ? selectedFeatureIndex : 0
    const selectedFeature = features[safeSelectedFeatureIndex]

    const groupCounts: Record<number, number> = {}

    features.forEach((feature) => {
        groupCounts[feature.featureGroup] = (groupCounts[feature.featureGroup] || 0) + 1
    })

    const shouldAddScroll = () =>
        Object.values(groupCounts).some(
            (count) => count > (portraitViewport ? scrollThreshold.portrait : scrollThreshold.landscape)
        )

    const allSymbolStyles = buildGradientStyle(layout.allSymbolsBackgroundColors)
    const isGuaranteed = isGuaranteedWinEnabled || isForceGuaranteedWinEnabled
    const displayedBalance = shouldBalanceSubtractOnly ? (subtractedBalance ?? balance) : balance

    const guaranteedWinBgStyle = buildGradientStyle(layout.guaranteedWinBackgroundColors, 'to bottom')
    const guaranteedWinActiveBgStyle = buildGradientStyle(layout.activeGuaranteedWinBackgroundColors, 'to bottom')
    const guaranteedWinBaseClasses =
        'bg-gradient-to-b rounded-[10px] text-center p-1 transition-all ease-in-out duration-350 mt-[20px]'
    const guaranteedWinBorderStyles = buildGradientBorderStyle(['#FFA146', '#FFF089', '#FFA146'], 2, 50)
    const togglePositionClasses = cn(
        'absolute top-[6px] left-[6px] transition-all ease-in-out duration-200',
        isGuaranteed ? 'left-[calc(100%-6px)] -translate-x-full' : 'translate-x-0'
    )
    const toggleBgStyle = buildGradientStyle(
        isGuaranteed ? ['#306611', '#52AF1C'] : layout.guaranteedWinToggleBackgroundColors
    )

    const symbolBorderColors = layout.symbolBorderColors ?? ['#FFA146', '#FFF089', '#FFA146']
    const selectedSymbolBorderColors = layout.selectedSymbolBorderColors ?? symbolBorderColors
    const symbolOptionWidth = !isMultiChoice ? '100%' : '240px'
    const symbolOptionHeight = !isMultiChoice ? '180px' : '290px'

    const betAmountBgStyle = buildGradientStyle(layout.amountInputBackgroundColors)

    const modalBodyPadding = !isMultiChoice ? '0' : '0 20px'

    const isBetAllowed = (bet: number) => {
        return displayedBalance >= bet && bet <= maxBet
    }

    useEffect(() => {
        setActiveBetIndex(activeIndex)
    }, [activeIndex])

    useEffect(() => {
        if (betOptions.length <= 0) return

        const feature = selectedFeature
        const multiplier = isGuaranteedWinEnabled
            ? (feature.guaranteedMultiplier ?? feature.multiplier)
            : feature.multiplier

        const bet = getBetValue(betOptions[activeBetIndex]) * multiplier

        setConfirmButtonDisabled(!isBetAllowed(bet))

        if (activeBetIndex <= 0) {
            setIsLeftArrowDisabled(true)
            setLeftArrowState(ArrowButtonState.DISABLED)
        } else {
            setIsLeftArrowDisabled(false)
            setLeftArrowState(ArrowButtonState.ENABLED)
        }

        if (activeBetIndex >= betOptions.length - 1) {
            setIsRightArrowDisabled(true)
            setRightArrowState(ArrowButtonState.DISABLED)

            setIsLeftArrowDisabled(false)
            setLeftArrowState(ArrowButtonState.ENABLED)
        } else {
            setIsRightArrowDisabled(false)
            setRightArrowState(ArrowButtonState.ENABLED)
        }
    }, [displayedBalance, betOptions, activeBetIndex, selectedFeatureIndex, isGuaranteed])

    useEffect(() => {
        if (!isOpen || viewport !== ViewportType.DESKTOP || !scrollRef.current) return

        const scrollContainer = scrollRef.current

        const handleWheel = (e: Event) => {
            const wheelEvent = e as WheelEvent

            if (wheelEvent.deltaY !== 0) {
                e.preventDefault()
                scrollContainer.scrollLeft += wheelEvent.deltaY
            }
        }

        scrollContainer.addEventListener('wheel', handleWheel, { passive: false })

        return () => {
            scrollContainer.removeEventListener('wheel', handleWheel)
        }
    }, [isOpen, viewport])

    const handleConfirmButtonClicked = () => {
        const selectedFeatureName = isGuaranteedWinEnabled
            ? (selectedFeature.guaranteedName ?? selectedFeature.name)
            : selectedFeature.name

        gameAnalytics.buyBonusConfirmed(selectedFeatureName)

        const setScrollingIndexMessage: EngineBetSliderSetScrollingIndexMessage = {
            type: GameUIMessageType.BET_SLIDER_SET_SCROLLING_INDEX, // TODO: rename engine message to separate from game ui message
            currentBetIndex: activeBetIndex,
            shouldPositionList: true,
        }

        let isDoubleChanceActivated = false

        window.postMessage(setScrollingIndexMessage)
        updateScrollToIndex(activeBetIndex)

        if (buyBonusConfig.shouldTheFeatureStayActive) {
            const multiplier = isGuaranteedWinEnabled
                ? (selectedFeature.guaranteedMultiplier ?? selectedFeature.multiplier)
                : selectedFeature.multiplier

            setMultiChoiceToggle(true)

            if (selectedFeature.featureGroup === FeatureGroup.ChancePlus) {
                isDoubleChanceActivated = true
                setMultiChoiceToggle(false)
                setDoubleChanceMultiplier(multiplier)
                setIsDoubleChanceActivated(true)
            }

            updateCurrentFeatureMultiplier(multiplier)
        }

        const confirmButtonClickedMessage: BuyBonusConfirmButtonClickedMessage = {
            type: GameUIMessageType.BUY_BONUS_CONFIRM_BUTTON_CLICKED,
            selectedFeatureIndex: safeSelectedFeatureIndex,
            betIndex: activeBetIndex,
            selectedFeatureName,
            scope: GameUIMessageScope.BUY_BONUS,
        }

        window.postMessage(confirmButtonClickedMessage)

        if (isDoubleChanceActivated) {
            const doubleChanceClickMessage: DoubleChanceButtonClickedMessage = {
                type: GameUIMessageType.DOUBLE_CHANCE_BUTTON_CLICKED,
                scope: GameUIMessageScope.DOUBLE_CHANCE,
            }

            window.postMessage(doubleChanceClickMessage)
        }

        setActiveBetIndex(activeIndex)

        onClose()
    }

    const handleGuaranteedButtonClicked = () => {
        setIsGuaranteedWinEnabled(!isGuaranteedWinEnabled)

        const guaranteeButtonClickedMessage: BuyBonusGuaranteeButtonClickedMessage = {
            type: GameUIMessageType.BUY_BONUS_GUARANTEE_BUTTON_CLICKED,
        }

        window.postMessage(guaranteeButtonClickedMessage)
    }

    const handleBuyBonusRejected = () => {
        gameAnalytics.buyBonusModalClosed()

        const buyBonusRejectedMessage: BuyBonusRejectedMessage = { type: BuyBonusMessages.BUY_BONUS_REJECTED }

        window.postMessage(buyBonusRejectedMessage)

        setActiveBetIndex(activeIndex)

        onClose()
    }

    const handleRightArrowPointerUp = () => {
        if (isRightArrowDisabled) return

        setRightArrowState(ArrowButtonState.ENABLED)

        setLeftArrowState(ArrowButtonState.ENABLED)
        setIsLeftArrowDisabled(false)
        setActiveBetIndex((prevIndex) => prevIndex + 1)

        const increaseButtonClickedMessage: BuyBonusIncreaseButtonClickedMessage = {
            type: GameUIMessageType.BUY_BONUS_INCREASE_BUTTON_CLICKED,
        }

        window.postMessage(increaseButtonClickedMessage)
    }

    const handleRightArrowPointerDown = () => {
        if (isRightArrowDisabled) return

        setRightArrowState(ArrowButtonState.PRESSED)
    }

    const handleRightArrowPointerEnter = () => {
        if (isRightArrowDisabled) return

        setRightArrowState(ArrowButtonState.HOVER)
    }

    const handleRightArrowPointerOut = () => {
        if (viewport !== ViewportType.DESKTOP) return

        setRightArrowState(isRightArrowDisabled ? ArrowButtonState.DISABLED : ArrowButtonState.ENABLED)
    }

    const handleLeftArrowPointerUp = () => {
        if (isLeftArrowDisabled) return

        setLeftArrowState(ArrowButtonState.ENABLED)
        setRightArrowState(ArrowButtonState.ENABLED)
        setIsRightArrowDisabled(false)

        setActiveBetIndex((prevIndex) => prevIndex - 1)

        const decreaseButtonClickedMessage: BuyBonusDecreaseButtonClickedMessage = {
            type: GameUIMessageType.BUY_BONUS_DECREASE_BUTTON_CLICKED,
        }

        window.postMessage(decreaseButtonClickedMessage)
    }

    const handleLeftArrowPointerDown = () => {
        if (isLeftArrowDisabled) return

        setLeftArrowState(ArrowButtonState.PRESSED)
    }

    const handleLeftArrowPointerEnter = () => {
        if (isLeftArrowDisabled) return

        setLeftArrowState(ArrowButtonState.HOVER)
    }

    const handleLeftArrowPointerOut = () => {
        if (viewport !== ViewportType.DESKTOP) return

        setLeftArrowState(isLeftArrowDisabled ? ArrowButtonState.DISABLED : ArrowButtonState.ENABLED)
    }

    const handleSelectedFeatureIndexClick = (index: number) => {
        setSelectedFeatureIndex(index)

        setSelectedFeature(features[index])

        setIsForceGuaranteedWinEnabled(features[index].guaranteedName === undefined)

        const optionButtonClickedMessage: BuyBonusOptionButtonClickedMessage = {
            type: GameUIMessageType.BUY_BONUS_OPTION_BUTTON_CLICKED,
            featureId: features[index].name,
        }

        window.postMessage(optionButtonClickedMessage)
    }

    // `index` is the global position in the flat `features` array (used for selection,
    // key and id). `imageIndex` is the position within the feature's own group, so the
    // per-group icon atlas frames stay 1-based (buy-bonus-image-card-01, chance-image-card-01, ...).
    const renderFeatureOption = (feature: BuyBonusFeature, index: number, imageIndex: number = index) => {
        const isSelected = index === safeSelectedFeatureIndex
        const symbolBackgroundStyles = buildGradientStyle(
            isSelected ? layout.activeSymbolBackgroundColors : layout.symbolBackgroundColors,
            'to bottom'
        )

        const featureGroup = feature.featureGroup ?? FeatureGroup.BuyBonus

        const imageData = findElementByImageName(`${groupToIconLabelMap[featureGroup]}${imageIndex + 1}.png`)

        const multiplier = isGuaranteedWinEnabled
            ? (feature.guaranteedMultiplier ?? feature.multiplier)
            : feature.multiplier
        const amount = getBetValue(activeBet) * multiplier
        const isDisabled = amount > displayedBalance || amount > maxBet

        return (
            <FeatureOption
                key={index}
                id={`bonus-choice-${index}`}
                onClick={() => handleSelectedFeatureIndexClick(index)}
                isDisabled={isDisabled}
                borderStyle={
                    buildGradientBorderStyle(
                        isSelected ? selectedSymbolBorderColors : symbolBorderColors,
                        isSelected ? 5 : 2,
                        10,
                        'to bottom'
                    ) as React.CSSProperties
                }
                symbolBackgroundStyles={symbolBackgroundStyles as React.CSSProperties}
                width={symbolOptionWidth}
                height={symbolOptionHeight}
                imageStyle={
                    imageData
                        ? (buildAtlasImageStyles(
                              imageData.atlasName,
                              imageData.value,
                              cachedImages[imageData.atlasName]
                          ) as React.CSSProperties)
                        : {}
                }
                label={feature.label}
                labelFontSize={feature.labelFontSize ?? '24px'}
                labelColor={
                    isSelected ? (layout.selectedSymbolLabelColor ?? layout.symbolLabelColor) : layout.symbolLabelColor
                }
                amountText={formatMoney(amount)}
                currency={currency}
                priceColor={
                    isSelected ? (layout.selectedSymbolPriceColor ?? layout.symbolPriceColor) : layout.symbolPriceColor
                }
                portraitViewport={portraitViewport}
            />
        )
    }

    const setArrowButtonBackgroundByState = (state: ArrowButtonState, type: ArrowButtonType) => {
        const buttonSourceData =
            findElementByImageName(`button-${type}-${state}.svg`) ||
            findElementByImageName(`button-${type}-${state}.png`)

        if (!buttonSourceData) {
            return
        }

        return buildAtlasImageStyles(
            buttonSourceData.atlasName,
            buttonSourceData.value,
            cachedImages[buttonSourceData.atlasName]
        )
    }

    const renderGuaranteedWinSection = () => {
        const isGuaranteedWinActive = selectedFeature?.guaranteedName !== undefined
        const activeGuaranteedWinLabelColor = layout.activeGuaranteedWinLabelColor ?? '#FFE365'
        const guaranteedWinLabelColor = layout.guaranteedWinLabelColor ?? '#FFC865'

        return (
            <div
                style={{
                    ...(isGuaranteed ? guaranteedWinActiveBgStyle : guaranteedWinBgStyle),
                    pointerEvents: isGuaranteedWinActive ? 'auto' : 'none',
                    opacity: isGuaranteedWinActive ? 1 : 0.5,
                }}
                className={guaranteedWinBaseClasses}
            >
                <div className="inline-flex items-center">
                    <span
                        className="mr-6 text-[38px] leading-[48px] font-bold"
                        style={{ color: isGuaranteed ? activeGuaranteedWinLabelColor : guaranteedWinLabelColor }}
                    >
                        {t('gameui.guaranteedWin')}
                    </span>
                    <div style={guaranteedWinBorderStyles} className="box-border rounded-[50px]">
                        <div
                            style={toggleBgStyle}
                            className="relative w-[132px] h-[62px] bg-gradient-to-r rounded-[50px] transition-all ease-in-out duration-350 cursor-pointer"
                            onClick={handleGuaranteedButtonClicked}
                        >
                            <img src={toggleCircle} className={togglePositionClasses} alt="GUARANTEED WIN toggle" />
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    const onPointerDownDesktop = (e: React.PointerEvent<HTMLDivElement>) => {
        if (!isDesktop) return

        const el = scrollRef.current

        if (!el) return

        isDownRef.current = true
        isDraggingRef.current = false
        cancelNextClickRef.current = false

        pointerIdRef.current = e.pointerId
        startXRef.current = e.clientX
        startScrollLeftRef.current = el.scrollLeft
    }

    const onPointerMoveDesktop = (e: React.PointerEvent<HTMLDivElement>) => {
        if (!isDesktop) return

        if (!isDownRef.current) return

        const el = scrollRef.current

        if (!el) return

        const dx = e.clientX - startXRef.current

        if (!isDraggingRef.current) {
            if (Math.abs(dx) < DRAG_THRESHOLD_X) return

            isDraggingRef.current = true
            cancelNextClickRef.current = true

            el.setPointerCapture(e.pointerId)
        }

        el.scrollLeft = startScrollLeftRef.current - dx
    }

    const stopDraggingDesktop = (e: React.PointerEvent<HTMLDivElement>) => {
        if (!isDesktop) return

        isDownRef.current = false

        if (isDraggingRef.current) {
            isDraggingRef.current = false

            try {
                scrollRef.current?.releasePointerCapture(e.pointerId)
            } catch {
                console.error()
            }
        }

        pointerIdRef.current = null
    }

    // If we dragged, kill the click for this interaction only.
    // This prevents accidental "click bonus box while dragging".
    const onClickCaptureDesktop = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!isDesktop) return

        if (cancelNextClickRef.current) {
            e.preventDefault()
            e.stopPropagation()
            cancelNextClickRef.current = false
        }
    }

    const sectionGradientStyle = buildGradientStyle(
        layout.sectionColors ?? layout.amountInputBackgroundColors,
        'to bottom'
    )
    const sectionBorderStyle = buildGradientBorderStyle(layout.sectionBorderColors ?? layout.headerColors, 1, 16)

    const renderMultiChoiceSection = (group = 1) => {
        return (
            <div
                style={sectionBorderStyle}
                className={cn('flex justify-center w-full min-w-[742px]', !portraitViewport && 'max-w-[1400px]')}
            >
                <div
                    style={{
                        ...allSymbolStyles,
                        ...sectionGradientStyle,
                        padding: portraitViewport ? '18px' : '10px',
                    }}
                    className={cn('flex flex-col item-center rounded-[16px] touch-manipulation', 'h-auto w-full')}
                >
                    <div
                        id={`multi-choice-modal-${group}`}
                        ref={scrollRef}
                        style={{
                            scrollbarWidth: 'none',
                            msOverflowStyle: 'none',
                        }}
                        className={cn(
                            'flex w-full flex-wrap justify-center gap-x-[7px] gap-y-[7px] touch-manipulation ',
                            'mb-[0px]'
                        )}
                        onPointerDown={isDesktop ? onPointerDownDesktop : undefined}
                        onPointerMove={isDesktop ? onPointerMoveDesktop : undefined}
                        onPointerUp={isDesktop ? stopDraggingDesktop : undefined}
                        onPointerCancel={isDesktop ? stopDraggingDesktop : undefined}
                        onPointerLeave={isDesktop ? stopDraggingDesktop : undefined}
                        onClickCapture={isDesktop ? onClickCaptureDesktop : undefined}
                    >
                        {(() => {
                            const indexStepOffset = features.filter((feat) => (feat.featureGroup ?? 0) < group).length

                            return features
                                .filter((feature) => feature.featureGroup === group)
                                .map((feature, index) => renderFeatureOption(feature, index + indexStepOffset, index))
                        })()}
                    </div>
                    {group === FeatureGroup.BuyBonus && hasGuaranteedWin && renderGuaranteedWinSection()}
                </div>
            </div>
        )
    }

    // TODO: should the feature name translations come from the buy bonus configuration or from localization
    const renderSingleChoiceSection = () => {
        const imageData = findElementByImageName('buy-bonus-image-card.png')
        const { labelColor, moneyColor } = layout
        const amount = getBetValue(activeBet) * selectedFeature.multiplier

        return (
            <div className="pb-2.5">
                {imageData && (
                    <div className="mb-2.5">
                        <div
                            style={buildAtlasImageStyles(
                                imageData.atlasName,
                                imageData.value,
                                cachedImages[imageData.atlasName]
                            )}
                        />
                    </div>
                )}
                <div className="text-4xl leading-none text-center">
                    <div
                        id="bonus-title"
                        className="mb-1 font-semibold [text-shadow:0_2px_2px_rgba(0,0,0,0.8)]"
                        style={{ color: labelColor }}
                    >
                        {selectedFeature.label}
                    </div>
                    <div
                        id="bonus-amount"
                        className="text-6xl font-black flex items-baseline justify-center"
                        style={{ color: moneyColor }}
                    >
                        <div>{formatMoney(amount)}</div>
                        <div className="font-medium ml-[5px] text-[36px]">{currency}</div>
                    </div>
                </div>
            </div>
        )
    }

    const headerGradientStyle = buildGradientStyle(layout.headerColors)
    const bodyGradientStyle = buildGradientStyle(layout.bodyColors, 'to bottom')
    const footerGradientStyle = buildGradientStyle(layout.footerColors)
    const betAmountBorderStyle = buildGradientStyle(layout.amountInputBorderColors, 'to bottom')
    const currentScale = !portraitViewport && isMultiChoice ? LANDSCAPE_SCALE : undefined

    return (
        <BonusModal
            isOpen={isOpen}
            onClose={handleBuyBonusRejected}
            showCloseIcon={true}
            width={layout.modalWidth}
            scale={currentScale}
        >
            {!isMultiChoice && (
                <BonusModalHeader gradientStyle={headerGradientStyle}>
                    <span id="bonus-name" className="text-[40px] font-bold" style={{ color: layout.headerLabelColor }}>
                        {t('gameui.buyBonus')}
                    </span>
                </BonusModalHeader>
            )}
            <BonusModalBody
                gradientStyle={
                    {
                        ...bodyGradientStyle,
                        ...(isMultiChoice ? { paddingTop: '0px' } : { paddingBottom: '24px' }),
                    } as React.CSSProperties
                }
            >
                <div
                    className={cn(
                        'w-full flex flex-col rounded-[16px] touch-manipulation',
                        'h-auto',
                        shouldAddScroll() &&
                            hasChancePlus &&
                            (portraitViewport ? 'min-h-[762px] max-h-[1080px]' : 'max-h-[666px]'),
                        shouldAddScroll() ? 'overflow-y-auto' : 'overflow-visible'
                    )}
                >
                    {isMultiChoice && (
                        <BonusModalHeader compact>
                            <span
                                id="bonus-name"
                                className="w-max inline-block text-[36px] font-bold rounded-t-lg px-[15px] leading-[45px] self-end"
                                style={{ color: layout.headerLabelColor, ...headerGradientStyle }}
                            >
                                {t('gameui.buyBonus')}
                            </span>
                        </BonusModalHeader>
                    )}
                    <div className="-m-px" id={`body-${FeatureGroup.BuyBonus}`}>
                        <div style={{ padding: modalBodyPadding }}>
                            {isMultiChoice ? renderMultiChoiceSection() : renderSingleChoiceSection()}
                        </div>
                    </div>
                    {hasChancePlus && (
                        <>
                            <BonusModalHeader compact>
                                <div className="pt-[30px]">
                                    <span
                                        id={`bonus-name-${FeatureGroup.ChancePlus}`}
                                        className="w-max inline-block self-start text-[36px] font-bold rounded-t-xl px-[15px] pb-[10px] leading-[45px] self-end"
                                        style={{ color: layout.headerLabelColor, ...headerGradientStyle }}
                                    >
                                        {t('gameui.chancePlus')}
                                    </span>
                                </div>
                            </BonusModalHeader>
                            <div className="-m-px" id={`body-${FeatureGroup.ChancePlus}`}>
                                <div style={{ padding: modalBodyPadding }}>
                                    {renderMultiChoiceSection(FeatureGroup.ChancePlus)}
                                </div>
                            </div>
                        </>
                    )}
                </div>

                <div className={cn('flex items-center justify-center', isMultiChoice && 'p-[15px]')}>
                    <button
                        id="bonus-arrow-left"
                        disabled={isLeftArrowDisabled}
                        onPointerDown={handleLeftArrowPointerDown}
                        onPointerUp={handleLeftArrowPointerUp}
                        onPointerEnter={handleLeftArrowPointerEnter}
                        onPointerOut={handleLeftArrowPointerOut}
                        style={{
                            ...setArrowButtonBackgroundByState(leftArrowState, ArrowButtonType.DECREASE),
                            marginRight: '10px',
                        }}
                    ></button>
                    <div
                        className="p-[4px] rounded-[18px] bg-gradient-to-b from-[#AB540C] via-[#FFF2B4] to-[#AB540C] inline-block"
                        style={{ ...betAmountBorderStyle }}
                    >
                        <div
                            id="bonus-active-bet-panel"
                            style={{ ...betAmountBgStyle }}
                            className="w-[300px] h-[115px] flex flex-col items-center justify-center rounded-[18px]"
                        >
                            <span
                                id="bonus-active-bet-currency"
                                className="mb-[10px] text-[18px] leading-[16px] font-bold text-[#FFD100]"
                            >
                                {currency}
                            </span>
                            <span id="bonus-active-bet-amount" className="mb-2 text-[46px] leading-none font-black">
                                {formatMoney(getBetValue(activeBet))}
                            </span>
                            <span
                                id="bonus-active-bet-title"
                                className="text-[24px] leading-[24px] font-bold text-[#FFD100]"
                            >
                                {t('gameui.bet')}
                            </span>
                        </div>
                    </div>
                    <button
                        id="bonus-arrow-right"
                        disabled={isRightArrowDisabled}
                        onPointerDown={handleRightArrowPointerDown}
                        onPointerUp={handleRightArrowPointerUp}
                        onPointerEnter={handleRightArrowPointerEnter}
                        onPointerOut={handleRightArrowPointerOut}
                        style={{
                            ...setArrowButtonBackgroundByState(rightArrowState, ArrowButtonType.INCREASE),
                            marginLeft: '10px',
                        }}
                    ></button>
                </div>
            </BonusModalBody>

            <BonusModalFooter gradientStyle={footerGradientStyle}>
                <button
                    id="bonus-buy-button"
                    className="mt-[10px]"
                    disabled={confirmButtonDisabled}
                    onClick={handleConfirmButtonClicked}
                >
                    <div className={cn('relative', confirmButtonDisabled && 'opacity-50')}>
                        <img src={buyButton} alt="Buy Bonus button" className="mx-auto" />
                        <div
                            className="absolute top-[50%] left-[50%] text-white font-bold text-3xl w-full"
                            style={{
                                transform: 'translate(-50%, -50%)',
                            }}
                        >
                            {t('gameui.buy')}
                        </div>
                    </div>
                </button>
                <div
                    id="bonus-balance-panel"
                    className="text-[24px] leading-none font-bold mb-[8px] flex items-baseline justify-center uppercase"
                    style={{ color: layout.balanceColor }}
                >
                    {`${t('common.balance')}: `}
                    <span className="text-[40px] leading-1 font-black mx-[8px]">{formatMoney(displayedBalance)}</span>
                    {currency}
                </div>
            </BonusModalFooter>
        </BonusModal>
    )
}

export default BuyBonusModal
